import React, { useEffect, useState, useRef } from "react";
import { DateRangePicker } from "react-date-range";
import { formatDMY, PrimaryColor } from "../common";
import { formatDateString } from "../pages/Dashboard/Auction/Auction";
import { enGB } from "date-fns/locale";
import ReactDOM from "react-dom";

interface DynamicDatePickerProps {
  setDate: (dates: { startDate: Date; endDate: Date; key: string }[]) => void;
  defaultDates?: { startDate: Date; endDate: Date; key: string };
  minDate?: string;
  maxDate?: string;
  selectedDates?:
    | { startDate: Date; endDate: Date; key: string }
    | Array<{ value: string; startDate?: Date; endDate?: Date }>;
  buttonClassName?: string;
}

// Format date with ordinal suffix (e.g., "23rd Feb 2025")
const formatDateWithOrdinal = (date: Date | undefined): string => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    return "";
  }

  const day = date.getDate();
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();

  // Determine ordinal suffix
  let suffix = "th";
  if (day === 1 || day === 21 || day === 31) {
    suffix = "st";
  } else if (day === 2 || day === 22) {
    suffix = "nd";
  } else if (day === 3 || day === 23) {
    suffix = "rd";
  }

  return `${day}${suffix} ${month} ${year}`;
};

export const DynamicDatePicker: React.FC<DynamicDatePickerProps> = ({
  setDate,
  defaultDates,
  selectedDates = [],
  minDate = "2021-01-01",
  maxDate = "2028-12-31",
  buttonClassName,
}) => {
  // `mounted` controls DOM presence; `isOpen` drives the CSS enter/leave classes
  const [mounted, setMounted] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [dateRange, setDateRange] = useState<{
    startDate: Date;
    endDate: Date;
    key: string;
  }>({
    startDate: new Date(),
    endDate: new Date(),
    key: "selection",
  });

  const [tempDateRange, setTempDateRange] = useState<{
    startDate: Date;
    endDate: Date;
    key: string;
  } | null>(null);

  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (defaultDates) {
      setDateRange(defaultDates);
    }
  }, [defaultDates]);

  const openPicker = () => {
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    setTempDateRange(dateRange);
    setMounted(true);
    // Defer so the element is in the DOM before we trigger the transition
    requestAnimationFrame(() => setIsOpen(true));
  };

  const closePicker = () => {
    setIsOpen(false);
    // Wait for the CSS transition to finish before unmounting
    closeTimeoutRef.current = setTimeout(() => setMounted(false), 250);
  };

  const handleDateChange = (item: any) => {
    setTempDateRange({
      startDate: item.selection.startDate || new Date(),
      endDate: item.selection.endDate || new Date(),
      key: item.selection.key || "selection",
    });
  };

  const handleSubmit = () => {
    if (tempDateRange) {
      const formattedStartDate =
        tempDateRange.startDate.toLocaleDateString("en-GB");
      const formattedEndDate =
        tempDateRange.endDate.toLocaleDateString("en-GB");

      const matchesDefault = defaultDates
        ? defaultDates.startDate.toLocaleDateString("en-GB") ===
            formattedStartDate &&
          defaultDates.endDate.toLocaleDateString("en-GB") === formattedEndDate
        : false;

      setDateRange(tempDateRange);

      if (!matchesDefault) {
        setDate([
          {
            startDate: new Date(
              formattedStartDate.split("/").reverse().join("-"),
            ),
            endDate: new Date(formattedEndDate.split("/").reverse().join("-")),
            key: "selection",
          },
        ]);
      }
    }

    closePicker();
  };

  // Compute all disabled dates
  const disabledDates: Date[] = [];

  if (typeof selectedDates === "object" && !Array.isArray(selectedDates)) {
    let current = new Date(selectedDates.startDate);
    const end = new Date(selectedDates.endDate);
    while (current <= end) {
      disabledDates.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
  } else if (Array.isArray(selectedDates)) {
    selectedDates.forEach((range) => {
      const date = String(range.value).slice(0, 21).split(",");
      let current = new Date(date[0]);
      const end = new Date(date[1]);
      while (current <= end) {
        disabledDates.push(new Date(current));
        current.setDate(current.getDate() + 1);
      }
    });
  }

  // Close picker when clicking outside
  useEffect(() => {
    if (!mounted) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;

      if (!target.isConnected) return;
      if (buttonRef.current?.contains(target)) return;

      const datePickerElements = document.querySelectorAll(
        ".date-selection-wrapper, .rdrDefinedRangesWrapper",
      );
      let clickedInside = false;

      datePickerElements.forEach((el) => {
        if (el.contains(target)) {
          clickedInside = true;
        }
      });

      if (!clickedInside) {
        closePicker();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [mounted]);

  const renderDatePickerPortal = () => {
    if (!mounted) return null;

    return ReactDOM.createPortal(
      <>
        <style>{`
          .dp-backdrop {
            transition: opacity 250ms ease;
          }
          .dp-backdrop-enter { opacity: 0; }
          .dp-backdrop-open  { opacity: 1; }

          .dp-modal {
            transition: opacity 250ms ease, transform 250ms ease;
          }
          .dp-modal-enter { opacity: 0; transform: scale(0.92) translateY(12px); }
          .dp-modal-open  { opacity: 1; transform: scale(1)    translateY(0);    }
        `}</style>

        {/* Backdrop */}
        <div
          className={`fixed inset-0 z-[9999998] dp-backdrop ${isOpen ? "dp-backdrop-open" : "dp-backdrop-enter"}`}
          style={{
            background: "rgba(0,0,0,0.45)",
            backdropFilter: "blur(2px)",
          }}
        />

        {/* Modal */}
        <div className="fixed inset-0 z-[9999999] flex items-center justify-center pointer-events-none">
          <div
            className={`dp-modal pointer-events-auto ${isOpen ? "dp-modal-open" : "dp-modal-enter"}`}
          >
            <div
              className="date-selection-wrapper shadow-2xl rounded-xl overflow-hidden"
              style={{ maxWidth: "96vw", maxHeight: "90vh", overflow: "auto" }}
            >
              <DateRangePicker
                onChange={handleDateChange}
                showPreview={true}
                dragSelectionEnabled={false}
                minDate={new Date(minDate)}
                maxDate={new Date(maxDate)}
                moveRangeOnFirstSelection={false}
                rangeColors={[PrimaryColor, PrimaryColor, PrimaryColor]}
                color={PrimaryColor}
                locale={enGB}
                months={2}
                ranges={
                  tempDateRange
                    ? [tempDateRange]
                    : [
                        {
                          startDate: dateRange.startDate || new Date(),
                          endDate: dateRange.endDate || new Date(),
                          key: "selection",
                        },
                      ]
                }
                direction="horizontal"
                disabledDates={disabledDates}
                inputRanges={[]}
              />
              <div className="flex justify-center gap-4 py-4 bg-white">
                <button
                  onClick={handleSubmit}
                  className="px-6 py-2 bg-[#F1935C] text-white rounded-lg"
                >
                  Submit
                </button>
              </div>
            </div>
          </div>
        </div>
      </>,
      document.body,
    );
  };

  const spanClassName = buttonClassName ? "" : "date-range";

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={() => (mounted ? closePicker() : openPicker())}
        className={buttonClassName || "date-range-button"}
      >
        {defaultDates ? (
          <span className={spanClassName}>
            {formatDateWithOrdinal(dateRange!.startDate)} -{" "}
            {formatDateWithOrdinal(dateRange!.endDate)}
          </span>
        ) : (
          <span className={spanClassName}>Add Date Range</span>
        )}
      </button>

      {renderDatePickerPortal()}
    </div>
  );
};
